import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { User, Mail, Lock, Smartphone, Building2 } from 'lucide-react';

const CreateAccountForm = ({ onBack }) => {
  const [error, setError] = useState(false);    
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    address: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation logic
    const { firstName, lastName, email, password, confirmPassword, phoneNumber } = formData;

    // Validate First Name and Last Name
    if (!firstName.trim() || !lastName.trim()) {
          setErrorMessage('First and last names cannot be empty.');     
     
      }
      setError(true);
      setIsSubmitting(false);
      
      // Hide the error after 5 seconds
      setTimeout(() => {
          setError(false);
          setErrorMessage('');
      }, 5000); // 5000 ms = 5 seconds

      // Check for Empty Fields
      if (!email) {
        setErrorMessage('Email field cannot be empty.');
        setError(true);
        setIsSubmitting(false);
        setTimeout(() => {
            setError(false);
            setErrorMessage('');
        }, 5000);
        return;
      }

      if (!password) {
        setErrorMessage('Password field cannot be empty.');
        setError(true);
        setIsSubmitting(false);
        setTimeout(() => {
            setError(false);
            setErrorMessage('');
        }, 5000);
        return;
      }

      if (!confirmPassword) {
        setErrorMessage('Please confirm your password.');
        setError(true);
        setIsSubmitting(false);
        setTimeout(() => {
            setError(false);
            setErrorMessage('');
        }, 5000);
        return;
      }

      if (!phoneNumber) {
        setErrorMessage('Phone number field cannot be empty.');
        setError(true);
        setIsSubmitting(false);
        setTimeout(() => {
            setError(false);
            setErrorMessage('');
        }, 5000);
        return;
      }

    // Validate Email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
        setErrorMessage('Please enter a valid email address.');
        setError(true);
        setIsSubmitting(false);
        setTimeout(() => {
            setError(false);
            setErrorMessage('');
        }, 5000);
        return;
    }

    // Validate Password
    const passwordRequirements = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,}$/;
    if (!password || !passwordRequirements.test(password)) {
        setErrorMessage('Password must be at least 6 characters long, contain at least one uppercase letter, one lowercase letter, one digit, and one special character.');
        setError(true);
        setIsSubmitting(false);
        setTimeout(() => {
            setError(false);
            setErrorMessage('');
        }, 5000);
        return;
    }

    // Confirm Password
    if (password !== confirmPassword) {
        setErrorMessage("Passwords do not match.");
        setError(true);
        setIsSubmitting(false);
        setTimeout(() => {
            setError(false);
            setErrorMessage('');
        }, 5000);
        return;
    }

    // Validate Phone Number
    const phoneRegex = /^(?:(?:\+?\d{1,3})[-.\s]?)?(?:\(?\d{1,4}\)?[-.\s]?)?\d{1,4}[-.\s]?\d{1,4}[-.\s]?\d{1,9}$/;
    if (phoneNumber && !phoneRegex.test(phoneNumber)) {
        setErrorMessage("Please enter a valid phone number.");
        setError(true);
        setIsSubmitting(false);
        setTimeout(() => {
            setError(false);
            setErrorMessage('');
        }, 5000);
        return;
    }

    //console.log("Form Submitted", formData);
    setIsSubmitting(true);
    try {
      const response = await fetch('auth/rgst', {
          method: 'POST',
          headers: {
              'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
      });
  
      // Check if the response was successful
      if (!response.ok) {
          // Attempt to parse JSON error response if exists
          const text = await response.text(); // Get the raw response text
          let errorMessage;
  
          try {
              const data = JSON.parse(text); // Try parsing it as JSON
              errorMessage = data.error || 'An unexpected error occurred'; // Set error message from server
          } catch (e) {
              // If parsing fails, return a static error message
              errorMessage = 'An unexpected error occurred. Please try again.'; // Static message
          }
  
          throw new Error(errorMessage); // Throw the error with the appropriate message
      }
  
      // Handle success response
      const data = await response.json(); // Parse the successful response
      alert(data.message); // Display the success message
       // Clear the form data on successful registration
       setIsSubmitting(false);
       setFormData({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
        phoneNumber: '',
        address: ''
    });
  
  } catch (error) {
      //console.error('Error:', error);
      setErrorMessage(error.message); // Using the error message from the caught error
      setError(true);
      setIsSubmitting(false);
      setTimeout(() => {
          setError(false);
          setErrorMessage('');
      }, 5000);
  
    }
};

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-6"
    >
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-white">Create Your Account</h2>
        <p className="text-gray-400">Start your secure journey with us</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          {/* First Name */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <User size={18} className="text-gray-400" />
            </div>
            <input 
              type="text" 
              name="firstName"
              placeholder="First Name"
              autoComplete='off'
              value={formData.firstName}
              onChange={handleInputChange}
              required
              className="w-full pl-10 px-4 py-2.5 bg-gray-900/50 border border-gray-700 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
            />
          </div>

          {/* Last Name */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <User size={18} className="text-gray-400" />
            </div>
            <input 
              type="text" 
              name="lastName"
              placeholder="Last Name"
              autoComplete='off'
              value={formData.lastName}
              onChange={handleInputChange}
              required
              className="w-full pl-10 px-4 py-2.5 bg-gray-900/50 border border-gray-700 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
            />
          </div>
        </div>

        {/* Email */}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Mail size={18} className="text-gray-400" />
          </div>
          <input 
            type="email" 
            name="email"
            autoComplete='off'
            placeholder="Email address"
            value={formData.email}
            onChange={handleInputChange}
            required
            className="w-full pl-10 px-4 py-2.5 bg-gray-900/50 border border-gray-700 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Password */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Lock size={18} className="text-gray-400" />
            </div>
            <input 
              type="password" 
              name="password"
              autoComplete='off'
              placeholder="Password"
              value={formData.password}
              onChange={handleInputChange}
              required
              className="w-full pl-10 px-4 py-2.5 bg-gray-900/50 border border-gray-700 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
            />
          </div>

          {/* Confirm Password */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Lock size={18} className="text-gray-400" />
            </div>
            <input 
              type="password" 
              name="confirmPassword"
              autoComplete='off'
              placeholder="Confirm Password"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              required
              className="w-full pl-10 px-4 py-2.5 bg-gray-900/50 border border-gray-700 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Phone Number */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Smartphone size={18} className="text-gray-400" />
            </div>
            <input 
              type="tel" 
              name="phoneNumber"
              placeholder="Phone Number"
              autoComplete='off'
              value={formData.phoneNumber}
              required
              onChange={handleInputChange}
              className="w-full pl-10 px-4 py-2.5 bg-gray-900/50 border border-gray-700 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
            />
          </div>

          {/* Company */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Building2 size={18} className="text-gray-400" />
            </div>
            <input 
              type="text" 
              name="address"
              placeholder="address (Optional)"
              autoComplete='off'
              value={formData.address}
              onChange={handleInputChange}
              className="w-full pl-10 px-4 py-2.5 bg-gray-900/50 border border-gray-700 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
            />
          </div>
        </div>

        <motion.button
          type="submit"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          disabled={isSubmitting}
          className="w-full py-2.5 bg-gradient-to-r from-teal-600 to-coral-600 text-white rounded-lg transition-all flex items-center justify-center space-x-2"
        >                                
          <User size={18} />
          <span>{isSubmitting ? 'Creating...' : 'Create Account'}</span>          
        </motion.button>
      </form>

      {error && (
                      <div
                        className="error-message"
                        style={{
                          color: 'red',
                          textAlign: 'center', // Center the text
                          width: '100%', // Full width to align horizontally in the container
                          margin: '10px 0', // Optional: Add some margin for spacing
                        }}
                      >
                        {errorMessage}
                      </div>
                    )}

      <div className="text-center text-sm">
        <button 
          onClick={onBack} 
          className="text-teal-400 hover:text-teal-300"
        >
          Back to Sign In
        </button>
      </div>
    </motion.div>
  );
};

export default CreateAccountForm;